import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaHeart, FaShoppingCart, FaEye, FaStar, FaRegStar, FaStarHalfAlt, FaShippingFast, FaFire } from 'react-icons/fa';

const ProductCard = ({ product, style = {} }) => {
  const navigate = useNavigate();
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [activeColor, setActiveColor] = useState(0);

  // Price calculations
  const price = parseFloat(product.price) || 0;
  const discount = product.discount > 0 ? product.discount : 0;
  const discountedPrice = discount > 0 ? price * (1 - discount / 100) : price;

  const handleProductClick = () => {
    navigate(`/product/${product.id}`);
  };

  const toggleWishlist = (e) => {
    e.stopPropagation();
    setIsWishlisted(!isWishlisted);
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(<FaStar key={i} className="star-filled" />);
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(<FaStarHalfAlt key={i} className="star-half" />);
      } else {
        stars.push(<FaRegStar key={i} className="star-empty" />);
      }
    }
    
    return stars;
  };

  return (
    <>
      <div 
        className={`product-card ${isHovered ? 'hovered' : ''}`}
        style={style}
        onClick={handleProductClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Image Container */}
        <div className="product-image-container">
          {!imageLoaded && (
            <div className="image-loader">
              <div className="loading-spinner"></div>
            </div>
          )}
          
          <img 
            src={product.images?.[0] || 'https://via.placeholder.com/400x300?text=No+Image'} 
            alt={product.name}
            className="product-image"
            onLoad={() => setImageLoaded(true)}
            loading="lazy"
          />
          
          {/* Discount Badge */}
          {discount > 0 && (
            <div className="discount-badge">
              <FaFire className="fire-icon" />
              {discount}% OFF
            </div>
          )}
          
          {/* Category Badge */}
          {product.category && (
            <div className="category-badge">
              {product.category}
            </div>
          )}
          
          

          {/* Overlay gradient */}
          <div className="image-overlay"></div>
        </div>
        
        {/* Product Info */}
        <div className="product-info">
          {/* Product Name */}
          <h3 className="product-name" title={product.name}>
            {product.name}
          </h3>
          
          {/* Brand */}
          {product.brand && (
            <p className="product-brand">{product.brand}</p>
          )}
          
          {/* Rating */}
          <div className="rating-container">
            <div className="stars">
              {renderStars(product.rating || 4.5)}
            </div>
            <span className="review-count">
              ({product.reviewCount || Math.floor(Math.random() * 50) + 10})
            </span>
          </div>
          
          {/* Description */}
          <p className="product-description">
            {product.description || 'Premium quality product with excellent features and modern design'}
          </p>
          
          {/* Price Section */}
          <div className="price-container">
            <div className="price-section">
              <span className="current-price">₹{discountedPrice.toFixed(0)}</span>
              {discount > 0 && (
                <span className="original-price">₹{price.toFixed(0)}</span>
              )}
            </div>
            
            {/* Free Shipping Badge */}
            <div className="shipping-badge">
              <FaShippingFast />
              <span>Free Shipping</span>
            </div>
          </div>
          
          {/* Color Variants */}
          {product.specifications?.colors && product.specifications.colors.length > 0 && (
            <div className="color-variants">
              <span className="colors-label">Colors:</span>
              <div className="color-options">
                {product.specifications.colors.slice(0, 5).map((color, index) => (
                  <button
                    key={index}
                    className={`color-option ${activeColor === index ? 'active' : ''}`}
                    style={{ backgroundColor: getColorValue(color) }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveColor(index);
                    }}
                    title={color}
                    aria-label={`Select ${color} color`}
                  />
                ))}
                {product.specifications.colors.length > 5 && (
                  <span className="more-colors">+{product.specifications.colors.length - 5}</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Hover Shine Effect */}
        <div className="shine-effect"></div>
      </div>

      {/* Enhanced CSS Styles */}
      <style jsx>{`
        .product-card {
          position: relative;
          background: linear-gradient(145deg, #ffffff, #f8f9fa);
          border-radius: 12px;
          overflow: hidden;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
          border: 1px solid rgba(0, 0, 0, 0.06);
          box-shadow:
            0 2px 8px rgba(0, 0, 0, 0.06),
            0 1px 3px rgba(0, 0, 0, 0.03);
          backdrop-filter: blur(10px);
          width: 100%;
          max-width: 300px;
          height: fit-content;
          display: flex;
          flex-direction: column;
        }

        .product-card:hover {
          transform: translateY(-4px) scale(1.02);
          box-shadow:
            0 8px 24px rgba(0, 0, 0, 0.12),
            0 4px 12px rgba(0, 0, 0, 0.08);
          border-color: rgba(102, 126, 234, 0.2);
        }

        .product-image-container {
          position: relative;
          aspect-ratio: 1/1;
          overflow: hidden;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .image-loader {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 2;
        }

        .loading-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid #667eea;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .product-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: all 0.6s cubic-bezier(0.25, 0.8, 0.25, 1);
          opacity: 0;
        }

        .product-image[src] {
          opacity: 1;
        }

        .product-card:hover .product-image {
          transform: scale(1.08);
        }

        .discount-badge {
          position: absolute;
          top: 8px;
          left: 8px;
          background: linear-gradient(135deg, #ff6b6b, #ee5a24);
          color: white;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 0.7rem;
          font-weight: 700;
          box-shadow: 0 2px 8px rgba(255, 107, 107, 0.3);
          z-index: 3;
          display: flex;
          align-items: center;
          gap: 2px;
          animation: pulse 2s infinite;
        }

        .fire-icon {
          font-size: 0.75rem;
          animation: flicker 1.5s ease-in-out infinite;
        }

        .category-badge {
          position: absolute;
          top: 8px;
          right: 8px;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          padding: 3px 8px;
          border-radius: 8px;
          font-size: 0.6rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          z-index: 3;
          backdrop-filter: blur(10px);
        }

        .action-buttons {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          display: flex;
          gap: 8px;
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
          z-index: 4;
        }

        .product-card:hover .action-buttons {
          opacity: 1;
          visibility: visible;
        }

        .action-buttons button {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.85rem;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }

        .wishlist-btn {
          background: rgba(255, 255, 255, 0.95);
          color: #ff6b6b;
        }

        .wishlist-btn.active {
          background: #ff6b6b;
          color: white;
        }

        .quick-view-btn {
          background: rgba(255, 255, 255, 0.95);
          color: #667eea;
        }

        .add-cart-btn {
          background: #667eea;
          color: white;
        }

        .action-buttons button:hover {
          transform: scale(1.1);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
        }

        .image-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            180deg,
            rgba(0, 0, 0, 0) 0%,
            rgba(0, 0, 0, 0.1) 100%
          );
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
        }

        .product-card:hover .image-overlay {
          opacity: 1;
        }

        .product-info {
          padding: 12px;
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex: 1;
        }

        .product-name {
          font-size: 0.9rem;
          font-weight: 700;
          color: #2d3436;
          margin: 0;
          line-height: 1.2;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          font-family: 'Playfair Display', serif;
        }

        .product-brand {
          font-size: 0.8rem;
          color: #636e72;
          margin: 0;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 500;
        }

        .rating-container {
          display: flex;
          align-items: center;
          gap: 6px;
          margin: 2px 0;
        }

        .stars {
          display: flex;
          gap: 1px;
        }

        .star-filled {
          color: #f39c12;
          font-size: 0.8rem;
        }

        .star-half {
          color: #f39c12;
          font-size: 0.8rem;
        }

        .star-empty {
          color: #ddd;
          font-size: 0.8rem;
        }

        .review-count {
          font-size: 0.75rem;
          color: #636e72;
          font-weight: 500;
        }

        .product-description {
          font-size: 0.8rem;
          color: #636e72;
          margin: 0;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .price-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 6px;
        }

        .price-section {
          display: flex;
          align-items: baseline;
          gap: 6px;
        }

        .current-price {
          font-size: 1rem;
          font-weight: 800;
          color: #2d3436;
          background: linear-gradient(135deg, #667eea, #764ba2);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .original-price {
          font-size: 0.9rem;
          color: #999;
          text-decoration: line-through;
          font-weight: 500;
        }

        .shipping-badge {
          display: flex;
          align-items: center;
          gap: 3px;
          font-size: 0.7rem;
          color: #00b894;
          font-weight: 600;
        }

        .color-variants {
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px solid #f1f3f5;
        }

        .colors-label {
          font-size: 0.75rem;
          color: #636e72;
          font-weight: 600;
          margin-bottom: 6px;
          display: block;
        }

        .color-options {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
        }

        .color-option {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          border: 2px solid transparent;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .color-option:hover {
          transform: scale(1.1);
          box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
        }

        .color-option.active {
          border-color: #667eea;
          box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.3);
        }

        .more-colors {
          font-size: 0.75rem;
          color: #636e72;
          font-weight: 500;
        }

        .shine-effect {
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.4),
            transparent
          );
          transition: left 0.6s ease;
          pointer-events: none;
        }

        .product-card:hover .shine-effect {
          left: 100%;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .product-card {
            border-radius: 16px;
          }

          .product-info {
            padding: 12px;
            gap: 4px;
          }

          .product-name {
            font-size: 0.9rem;
          }

          .current-price {
            font-size: 1rem;
          }

          .action-buttons {
            opacity: 1;
            visibility: visible;
            bottom: 12px;
            top: auto;
            left: 50%;
            transform: translateX(-50%);
            background: rgba(255, 255, 255, 0.95);
            padding: 6px;
            border-radius: 20px;
            backdrop-filter: blur(10px);
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
          }

          .action-buttons button {
            width: 36px;
            height: 36px;
            font-size: 0.9rem;
          }

          .shipping-badge {
            font-size: 0.65rem;
          }

          .product-description {
            -webkit-line-clamp: 1;
            font-size: 0.75rem;
          }

          .product-brand {
            font-size: 0.75rem;
          }

          .colors-label {
            font-size: 0.7rem;
          }

          .color-option {
            width: 18px;
            height: 18px;
          }
        }

        @media (max-width: 480px) {
          .product-card {
            max-width: 220px;
            border-radius: 8px;
          }

          .product-info {
            padding: 8px;
            gap: 2px;
          }

          .product-name {
            font-size: 0.8rem;
            -webkit-line-clamp: 1;
          }

          .current-price {
            font-size: 0.85rem;
          }

          .discount-badge {
            padding: 2px 4px;
            font-size: 0.6rem;
            top: 4px;
            left: 4px;
          }

          .category-badge {
            padding: 1px 4px;
            font-size: 0.5rem;
            top: 4px;
            right: 4px;
          }

          .price-container {
            flex-direction: column;
            align-items: flex-start;
            gap: 2px;
          }

          .action-buttons {
            bottom: 6px;
            padding: 3px;
            gap: 3px;
          }

          .action-buttons button {
            width: 24px;
            height: 24px;
            font-size: 0.7rem;
          }

          .color-option {
            width: 14px;
            height: 14px;
          }

          .shipping-badge {
            font-size: 0.55rem;
          }

          .product-brand {
            font-size: 0.65rem;
          }

          .product-description {
            font-size: 0.65rem;
          }

          .star-filled, .star-half, .star-empty {
            font-size: 0.7rem;
          }

          .review-count {
            font-size: 0.65rem;
          }
        }

        @media (max-width: 360px) {
          .product-card {
            max-width: 150px;
            border-radius: 6px;
          }

          .product-info {
            padding: 6px;
            gap: 1px;
          }

          .product-name {
            font-size: 0.75rem;
            line-height: 1.1;
          }

          .current-price {
            font-size: 0.8rem;
          }

          .action-buttons {
            bottom: 4px;
            padding: 2px;
            gap: 2px;
          }

          .action-buttons button {
            width: 20px;
            height: 20px;
            font-size: 0.6rem;
          }

          .discount-badge {
            padding: 1px 3px;
            font-size: 0.5rem;
            top: 3px;
            left: 3px;
          }

          .category-badge {
            padding: 1px 3px;
            font-size: 0.45rem;
            top: 3px;
            right: 3px;
          }

          .shipping-badge {
            font-size: 0.5rem;
          }

          .color-option {
            width: 12px;
            height: 12px;
          }
        }

        /* Animations */
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        @keyframes flicker {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
      `}</style>
    </>
  );
};

// Helper function to convert color names to values
const getColorValue = (colorName) => {
  const colorMap = {
    'black': '#1a1a1a',
    'white': '#ffffff',
    'red': '#e74c3c',
    'blue': '#3498db',
    'green': '#27ae60',
    'yellow': '#f1c40f',
    'silver': '#bdc3c7',
    'gold': '#f39c12',
    'rose gold': '#e8b4b8',
    'navy': '#2c3e50',
    'brown': '#8b4513',
    'gray': '#7f8c8d',
    'grey': '#7f8c8d',
    'pink': '#e91e63',
    'purple': '#9b59b6',
    'orange': '#e67e22',
    'beige': '#f5f5dc',
    'teal': '#1abc9c',
    'burgundy': '#8e2a2a'
  };
  
  return colorMap[colorName.toLowerCase()] || colorName;
};

export default ProductCard;
